import React, { useContext, useState } from 'react';
import { PlantContext } from '@/contexts/PlantContext';
import PlantSwitchConfirmDialog from './PlantSwitchConfirmDialog';
import { Button, Dropdown, Spin, Tooltip, Divider } from 'antd';
import { Building2, Globe, Factory, ChevronDown } from 'lucide-react';
import { defaultColors } from '@/themes/antd-theme';

const PlantSelector = () => {
  const { plants, activePlant, userType, switchPlant, loading } = useContext(PlantContext);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [targetPlant, setTargetPlant] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center p-2">
        <Spin size="small" />
      </div>
    );
  }

  if (!activePlant) return null;
  const canSwitch = plants.length > 1;

  const handlePlantChangeRequest = ({ key }) => {
    const selectedPlant = plants.find(p => p.id.toString() === key);

    // Don't show dialog if selecting the same plant
    if (selectedPlant.id === activePlant.id) {
      return;
    }

    // For global users, show confirmation dialog
    if (userType === 'global') {
      setTargetPlant(selectedPlant);
      setConfirmDialogOpen(true);
    } else {
      // For plant-specific users (shouldn't happen), just try to switch
      switchPlant(selectedPlant);
    }
  };

  const handleConfirmSwitch = () => {
    if (targetPlant) {
      switchPlant(targetPlant);
    }
    setConfirmDialogOpen(false);
  };

  const handleCancelSwitch = () => {
    setConfirmDialogOpen(false);
    setTargetPlant(null);
  };

  const getUserTypeIcon = () => {
    return userType === 'global' ? <Globe size={16} /> : <Building2 size={16} />;
  };

  const getUserTypeLabel = () => {
    return userType === 'global' ? 'Global Access' : 'Plant Access';
  };

  const menuItems = {
    items: plants?.map((plant) => ({
      key: plant.id.toString(),
      label: (
        <div className="flex items-center gap-3 py-1">
          <Factory
            size={20}
            style={{ color: plant.id === activePlant.id ? defaultColors.primary : defaultColors.text.secondary }}
          />
          <div className="flex-1">
            <div
              className="text-sm font-semibold"
              style={{ color: plant.id === activePlant.id ? defaultColors.primary : undefined }}
            >
              {plant.code}
            </div>
            <div className="text-xs text-gray-600">{plant.name}</div>
            <div className="text-xs text-gray-500">{plant.city}, {plant.state}</div>
          </div>
        </div>
      ),
      style: {
        borderLeft: plant.id === activePlant.id ? `3px solid ${defaultColors.primary}` : 'none',
        backgroundColor: plant.id === activePlant.id ? `${defaultColors.primary}14` : 'transparent',
      }
    })),
    onClick: handlePlantChangeRequest
  };

  return (
    <>
      <Tooltip
        zIndex={9999}
        title={
          <div>
            <div className="text-xs font-semibold">{getUserTypeLabel()}</div>
            <div className="text-xs">{activePlant.name}</div>
            <div className="text-xs">{activePlant.city}, {activePlant.state}</div>
            {canSwitch && (
              <div className="text-xs italic mt-1">Click to switch plant</div>
            )}
          </div>
        }
      >
        <Dropdown menu={menuItems} trigger={['click']} disabled={!canSwitch}>
          <Button
            disabled={!canSwitch}
            icon={getUserTypeIcon()}
            className="flex items-center gap-1"
            style={{
              borderRadius: '8px',
              backgroundColor: `${defaultColors.primary}14`,
              borderColor: `${defaultColors.primary}33`,
              color: defaultColors.primary,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            <div className="flex items-center gap-1">
              <Factory size={18} />
              <span className="font-semibold text-sm whitespace-nowrap">
                {activePlant.code}
              </span>
            </div>
            {canSwitch && <ChevronDown size={16} />}
          </Button>
        </Dropdown>
      </Tooltip>

      {/* Confirmation Dialog */}
      <PlantSwitchConfirmDialog
        open={confirmDialogOpen}
        currentPlant={activePlant}
        targetPlant={targetPlant}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
      />
    </>
  );
};

export default PlantSelector;
